import { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, TrendingDown, Users, ShoppingCart, DollarSign, Star, Clock, BarChart3, PieChart, Wallet, Gift, Flame, CalendarRange } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminAnalytics() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [popularItems, setPopularItems] = useState([]);
  const [segments, setSegments] = useState(null);
  const [revenueByTime, setRevenueByTime] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [clv, setClv] = useState(null);
  const [liability, setLiability] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const fetchedRef = useRef(false);

  const fetchAllAnalytics = useCallback(async () => {
    try {
      const [popularRes, segmentsRes, revenueRes, comparativeRes, clvRes, liabRes, heatRes, cohortRes, campRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/popular-items?days=30`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/customer-segments`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/revenue-by-time?days=7`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/comparative`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/clv`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/liability`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/heatmap?days=30`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/cohort-retention`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        }),
        fetch(`${API_URL}/api/admin/analytics/campaigns`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
        })
      ]);

      if (popularRes.ok) setPopularItems(await popularRes.json());
      if (segmentsRes.ok) setSegments(await segmentsRes.json());
      if (revenueRes.ok) setRevenueByTime(await revenueRes.json());
      if (comparativeRes.ok) setComparative(await comparativeRes.json());
      if (clvRes.ok) setClv(await clvRes.json());
      if (liabRes.ok) setLiability(await liabRes.json());
      if (heatRes.ok) setHeatmap(await heatRes.json());
      if (cohortRes.ok) setCohorts(await cohortRes.json());
      if (campRes.ok) setCampaigns(await campRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchAllAnalytics();
  }, [fetchAllAnalytics, token]);

  const segmentColors = {
    new: 'bg-blue-100 text-blue-700',
    returning: 'bg-green-100 text-green-700',
    loyal: 'bg-purple-100 text-purple-700',
    vip: 'bg-amber-100 text-amber-700',
    at_risk: 'bg-orange-100 text-orange-700',
    churned: 'bg-red-100 text-red-700'
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="h-40" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          Analytics & Insights
        </h1>
        <p className="text-slate-500 mt-1">Business intelligence and performance metrics</p>
      </div>

      {/* Comparative Stats */}
      {comparative && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="This Week Revenue"
            value={`R${comparative.this_week.revenue.toLocaleString()}`}
            change={comparative.changes.revenue_percentage}
            icon={DollarSign}
          />
          <StatCard
            title="This Week Orders"
            value={comparative.this_week.orders}
            change={comparative.changes.orders_percentage}
            icon={ShoppingCart}
          />
          <StatCard
            title="Average Order Value"
            value={`R${(comparative.this_week.revenue / comparative.this_week.orders || 0).toFixed(0)}`}
            icon={TrendingUp}
          />
          <StatCard
            title="Total Customers"
            value={segments?.summary.total_customers || 0}
            icon={Users}
          />
        </div>
      )}

      <Tabs defaultValue="popular" className="space-y-4">
        <TabsList>
          <TabsTrigger value="popular">Popular Items</TabsTrigger>
          <TabsTrigger value="segments">Customer Segments</TabsTrigger>
          <TabsTrigger value="revenue">Revenue by Time</TabsTrigger>
          <TabsTrigger value="clv">Top Customers</TabsTrigger>
          <TabsTrigger value="liability" data-testid="tab-liability">Liability</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="tab-heatmap">Peak Hours</TabsTrigger>
          <TabsTrigger value="retention" data-testid="tab-retention">Retention</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns</TabsTrigger>
        </TabsList>

        {/* Popular Items */}
        <TabsContent value="popular">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-amber-500" />
                Most Popular Items (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {popularItems.slice(0, 10).map((item, idx) => (
                  <div key={item._id} className="flex items-center gap-4">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-slate-500">{item.order_count} orders</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">R{item.total_revenue?.toFixed(0)}</p>
                      <p className="text-sm text-slate-500">{item.total_quantity} sold</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Segments */}
        <TabsContent value="segments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Customer Segmentation
              </CardTitle>
              <CardDescription>
                Customers grouped by order frequency and recency
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                {segments && Object.entries(segments.summary).filter(([k]) => k !== 'total_customers').map(([segment, count]) => (
                  <div key={segment} className={`p-4 rounded-lg ${segmentColors[segment]} text-center`}>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm capitalize">{segment.replace('_', ' ')}</p>
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                {segments && Object.entries(segments.segments).map(([segment, customers]) => (
                  <div key={segment}>
                    <h4 className="font-medium capitalize mb-2">{segment.replace('_', ' ')} Customers</h4>
                    <div className="grid gap-2">
                      {customers.slice(0, 3).map((customer) => (
                        <div key={customer._id} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                          <div>
                            <p className="font-medium">{customer.customer_name}</p>
                            <p className="text-xs text-slate-500">{customer.customer_email}</p>
                          </div>
                          <div className="text-right text-sm">
                            <p>{customer.total_orders} orders</p>
                            <p className="text-slate-500">R{customer.total_spent?.toFixed(0)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Revenue by Time */}
        <TabsContent value="revenue">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Revenue by Hour
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {revenueByTime && Object.entries(revenueByTime.by_hour)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([hour, data]) => (
                      <div key={hour} className="flex items-center gap-2">
                        <span className="w-12 text-sm text-slate-500">{hour}:00</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{
                              width: `${Math.min(100, (data.orders / Math.max(...Object.values(revenueByTime.by_hour).map(d => d.orders))) * 100)}%`
                            }}
                          />
                        </div>
                        <span className="w-20 text-right text-sm">
                          {data.orders} / R{data.revenue.toFixed(0)}
                        </span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-primary" />
                  Revenue by Day
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {revenueByTime && Object.entries(revenueByTime.by_day)
                    .sort(([, a], [, b]) => b.revenue - a.revenue)
                    .map(([day, data]) => (
                      <div key={day} className="flex items-center justify-between">
                        <span className="font-medium">{day}</span>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline">{data.orders} orders</Badge>
                          <span className="font-bold text-primary">R{data.revenue.toFixed(0)}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Customer Lifetime Value */}
        <TabsContent value="clv">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                Customer Lifetime Value
              </CardTitle>
              <CardDescription>
                Top customers by total spending
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clv && (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">Total CLV</p>
                      <p className="text-2xl font-bold text-green-700">R{clv.summary.total_clv.toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-600">Average CLV</p>
                      <p className="text-2xl font-bold text-blue-700">R{clv.summary.average_clv.toFixed(0)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {clv.top_customers.slice(0, 10).map((customer, idx) => (
                      <div key={customer._id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium">{customer.customer_name}</p>
                          <p className="text-sm text-slate-500">{customer.customer_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">R{customer.total_spent?.toFixed(0)}</p>
                          <p className="text-sm text-slate-500">
                            {customer.total_orders} orders • Avg R{customer.avg_order_value?.toFixed(0)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Liability */}
        <TabsContent value="liability">
          <Card data-testid="liability-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Wallet &amp; Voucher Liability</CardTitle>
              <CardDescription>Prepaid money owed to customers + unredeemed gift-voucher value</CardDescription>
            </CardHeader>
            <CardContent>
              {liability && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-rose-50 p-4 rounded-lg">
                      <p className="text-sm text-rose-600">Total Liability</p>
                      <p className="text-2xl font-bold text-rose-700" data-testid="total-liability">R{liability.total_liability.toLocaleString()}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-600">Wallet Balances</p>
                      <p className="text-2xl font-bold text-blue-700">R{liability.wallet_liability.toLocaleString()}</p>
                      <p className="text-xs text-blue-500">{liability.wallet_holders} holders</p>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <p className="text-sm text-amber-600">Unredeemed Vouchers</p>
                      <p className="text-2xl font-bold text-amber-700">R{liability.voucher_outstanding_value.toLocaleString()}</p>
                      <p className="text-xs text-amber-500">{liability.voucher_outstanding_count} active</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <Gift className="h-8 w-8 text-primary" />
                    <div className="flex-1">
                      <p className="font-medium">Voucher redemption rate</p>
                      <p className="text-sm text-slate-500">{liability.voucher_redeemed_count} of {liability.voucher_issued_count} issued redeemed (R{liability.voucher_redeemed_value.toLocaleString()})</p>
                    </div>
                    <span className="text-2xl font-bold text-primary">{liability.voucher_redemption_rate}%</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Peak-hour heatmap */}
        <TabsContent value="heatmap">
          <Card data-testid="heatmap-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Flame className="h-5 w-5 text-orange-500" /> Peak-hour Heatmap</CardTitle>
              <CardDescription>Order volume by day &amp; hour (last {heatmap?.days || 30} days) — plan kitchen staffing</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {heatmap && (() => {
                const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                const max = heatmap.peak?.count || 1;
                return (
                  <table className="text-[10px] border-collapse">
                    <thead>
                      <tr>
                        <th className="w-10"></th>
                        {Array.from({ length: 24 }, (_, h) => (
                          <th key={h} className="px-0.5 text-slate-400 font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.grid.map((row, d) => (
                        <tr key={d}>
                          <td className="pr-2 text-slate-600 font-medium">{dayNames[d]}</td>
                          {row.map((c, h) => {
                            const intensity = c / max;
                            const bg = c === 0 ? '#f1f5f9' : `rgba(200,16,46,${0.15 + intensity * 0.85})`;
                            return <td key={h} title={`${dayNames[d]} ${h}:00 — ${c} orders`} style={{ background: bg }} className="w-5 h-5 text-center text-white">{c > 0 ? c : ''}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
              {heatmap?.peak && (
                <p className="mt-4 text-sm text-slate-600">Busiest: <strong>{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][heatmap.peak.day]} at {heatmap.peak.hour}:00</strong> ({heatmap.peak.count} orders)</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cohort retention */}
        <TabsContent value="retention">
          <Card data-testid="retention-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5 text-purple-500" /> Cohort Retention</CardTitle>
              <CardDescription>% of each signup-month cohort that ordered again in later months</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {cohorts?.cohorts?.length > 0 ? (
                <table className="text-xs border-collapse w-full">
                  <thead>
                    <tr className="text-slate-400">
                      <th className="text-left py-1 pr-3">Cohort</th>
                      <th className="text-left py-1 pr-3">Size</th>
                      {cohorts.cohorts[0].retention.map((_, i) => <th key={i} className="px-2 py-1">M{i}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cohorts.cohorts.map((c) => (
                      <tr key={c.cohort} className="border-t border-slate-100">
                        <td className="py-1.5 pr-3 font-medium text-slate-700">{c.cohort}</td>
                        <td className="py-1.5 pr-3 text-slate-500">{c.size}</td>
                        {c.retention.map((v, i) => (
                          <td key={i} className="px-1 py-1 text-center text-white font-medium" style={{ background: `rgba(124,58,237,${0.15 + (v / 100) * 0.85})` }}>{v}%</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-slate-500 text-sm">Not enough order history yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaign analytics */}
        <TabsContent value="campaigns">
          <Card data-testid="campaigns-panel">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Gift className="h-5 w-5 text-primary" /> Gift-Voucher Campaigns</CardTitle>
              <CardDescription>Payday/holiday nudges and voucher purchases attributed within 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              {campaigns?.campaigns?.length > 0 ? (
                <div className="space-y-3">
                  {campaigns.campaigns.map((c, i) => (
                    <div key={`${c.label}-${c.sent_at || i}`} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{c.label}</p>
                        <p className="text-sm text-slate-500">{c.sent_at ? new Date(c.sent_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : ''} · {c.recipients} recipients</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">{c.attributed_purchases} purchases</p>
                        <p className="text-sm text-slate-500">R{c.attributed_value?.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-slate-500 text-sm">No campaigns sent yet. Trigger one from System Settings → Gift Voucher Campaign.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ title, value, change, icon: Icon }) {
  const isPositive = change >= 0;
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className="h-5 w-5 text-slate-400" />
          {change !== undefined && (
            <Badge variant={isPositive ? 'default' : 'destructive'} className={isPositive ? 'bg-green-100 text-green-700' : ''}>
              {isPositive ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {Math.abs(change).toFixed(1)}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-slate-500">{title}</p>
      </CardContent>
    </Card>
  );
}
